import type { CSSProperties, ReactNode } from "react";

export type CalloutStyle = "info" | "warning" | "error" | "success";

export interface CalloutProps {
	readonly style?: CalloutStyle;
	readonly title?: ReactNode;
	readonly message?: ReactNode;
	readonly symbol?: ReactNode;
	readonly actionLabel?: ReactNode;
	readonly onAction?: () => void;
	readonly className?: string;
}

/** Resolve a foreground + border + background tint per callout style. */
function calloutColors(variant: CalloutStyle): {
	color: string;
	border: string;
	background: string;
} {
	switch (variant) {
		case "error":
			return {
				color: "var(--ds-destructive)",
				border: "color-mix(in srgb, var(--ds-destructive) 42%, transparent)",
				background:
					"color-mix(in srgb, var(--ds-destructive) 8%, var(--ds-surface))",
			};
		case "warning":
			return {
				color: "color-mix(in srgb, orange 78%, var(--ds-text-primary))",
				border: "color-mix(in srgb, orange 38%, transparent)",
				background: "color-mix(in srgb, orange 8%, var(--ds-surface))",
			};
		case "success":
			return {
				color: "color-mix(in srgb, green 72%, var(--ds-text-primary))",
				border: "color-mix(in srgb, green 38%, transparent)",
				background: "color-mix(in srgb, green 8%, var(--ds-surface))",
			};
		default:
			return {
				color: "var(--ds-accent)",
				border: "color-mix(in srgb, var(--ds-accent) 35%, transparent)",
				background:
					"color-mix(in srgb, var(--ds-accent) 8%, var(--ds-surface))",
			};
	}
}

/**
 * Callout — inline advisory block (info / warning / error / success). Mirrors
 * `DSCallout` on iOS with an optional action button.
 */
export function Callout({
	style: variant = "info",
	title,
	message,
	symbol,
	actionLabel,
	onAction,
	className = "",
}: Readonly<CalloutProps>) {
	const colors = calloutColors(variant);
	const sectionStyle: CSSProperties = {
		color: colors.color,
		border: `1px solid ${colors.border}`,
		background: colors.background,
	};
	return (
		<section
			className={`ds-callout ds-callout--${variant}${
				symbol === undefined ? " ds-callout--no-symbol" : ""
			} ${className}`.trim()}
			style={sectionStyle}
		>
			{symbol === undefined ? null : <span aria-hidden="true">{symbol}</span>}
			<div>
				{title === undefined ? null : <strong>{title}</strong>}
				{message === undefined ? null : <p>{message}</p>}
				{actionLabel === undefined ? null : (
					<button
						type="button"
						onClick={onAction}
						style={{
							marginTop: "var(--ds-space-sm)",
							color: colors.color,
							background: "none",
							border: 0,
							padding: 0,
							fontWeight: 600,
							cursor: "pointer",
						}}
					>
						{actionLabel}
					</button>
				)}
			</div>
		</section>
	);
}
